/**
 * Go to… — find any page in the menu by typing (Ctrl/⌘ K, or "/").
 *
 * Eaze has a lot in it; the menu only shows a business what it uses, and
 * this is the fast way to reach any of it. Built from the same menu model
 * as the sidebar (useNavModel), so it offers exactly what this person may
 * open here — nothing locked, nothing from another industry.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import useBusinessStore from "@/stores/businessStore";
import { useNavModel } from "./Sidebar";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export default function QuickFind() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const navigate = useNavigate();
  const leaveBusiness = useBusinessStore((s) => s.setActiveBusinessSlug);
  const { navGroups, labelFor } = useNavModel();
  const listRef = useRef<HTMLUListElement>(null);

  // Ctrl/⌘ K anywhere; "/" when not typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement)?.tagName || "") || (e.target as HTMLElement)?.isContentEditable;
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const all = useMemo(
    () => navGroups.flatMap((g) => g.items.filter((it) => !it.locked).map((it) => ({ it, label: labelFor(it), section: g.header }))),
    [navGroups, labelFor],
  );
  const results = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return all;
    return all
      .map((r) => {
        const hay = `${r.label} ${r.section}`.toLowerCase();
        if (!words.every((w) => hay.includes(w))) return null;
        // Names that start with what was typed come first.
        const score = r.label.toLowerCase().startsWith(words[0]) ? 0 : r.label.toLowerCase().includes(words[0]) ? 1 : 2;
        return { ...r, score };
      })
      .filter((r): r is NonNullable<typeof r> => !!r)
      .sort((a, b) => a.score - b.score);
  }, [all, q]);

  useEffect(() => setAt(0), [q, open]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-i="${at}"]`)?.scrollIntoView({ block: "nearest" });
  }, [at]);

  const go = (i: number) => {
    const r = results[i];
    if (!r) return;
    if (r.it.allBusinesses) leaveBusiness(null);
    setOpen(false);
    setQ("");
    navigate(r.it.path);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 items-center gap-2 rounded-md border border-border bg-muted/40 px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary lg:w-56"
        aria-label="Go to a page"
        aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
      >
        <Search className="h-4 w-4 shrink-0" aria-hidden />
        <span className="hidden flex-1 text-left lg:inline">Go to…</span>
        <kbd className="hidden rounded border border-border bg-background px-1.5 text-[10px] font-medium lg:inline">{isMac ? "⌘K" : "Ctrl K"}</kbd>
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <DialogContent className="top-[12vh] max-w-lg translate-y-0 gap-0 overflow-hidden p-0 sm:top-[15vh]">
          <DialogTitle className="sr-only">Go to a page</DialogTitle>
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setAt((i) => Math.min(results.length - 1, i + 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
                else if (e.key === "Enter") { e.preventDefault(); go(at); }
              }}
              placeholder="Type a page — sales, stock, payroll…"
              className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              role="combobox"
              aria-expanded
              aria-controls="quickfind-list"
              aria-activedescendant={results[at] ? `qf-${at}` : undefined}
              aria-label="Page name"
            />
          </div>
          <ul ref={listRef} id="quickfind-list" role="listbox" className="max-h-[min(60vh,420px)] overflow-y-auto p-1.5">
            {results.length === 0 ? (
              <li className="px-3 py-8 text-center text-sm text-muted-foreground">Nothing called “{q}” here.</li>
            ) : (
              results.map((r, i) => {
                const Icon = r.it.icon;
                return (
                  <li
                    key={`${r.section}-${r.it.path}`}
                    id={`qf-${i}`}
                    data-i={i}
                    role="option"
                    aria-selected={i === at}
                    onMouseMove={() => setAt(i)}
                    onClick={() => go(i)}
                    className={cn("flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm", i === at ? "bg-accent text-accent-foreground" : "text-foreground")}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="flex-1 truncate">{r.label}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{r.section.charAt(0) + r.section.slice(1).toLowerCase()}</span>
                    {i === at && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
                  </li>
                );
              })
            )}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
